import React from 'react';
import moment from 'moment';
import NumberFormat from 'react-number-format';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import {
  getReservationChannelLabel,
  getReservationSourceLabel
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';
import {
  AdType,
  MarketingChannel,
  getAdTypeLabel,
  getMarketingChannelLabel,
  messagingChannelLabels
} from '../../../../../shared/interfaces/attribution.interface';

export interface SourceSectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
}

const Item = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="min-w-0">
    <div className="text-[14px] text-neutral-300">{label}</div>
    <div
      className={`text-[15px] truncate ${
        value ? 'font-gilroy-medium text-neutral-500' : 'text-neutral-300'
      }`}
      title={typeof value === 'string' ? value : undefined}
    >
      {value || '—'}
    </div>
  </div>
);

const chipClassName =
  'px-[10px] py-[3px] rounded-full text-[12px] font-gilroy-medium whitespace-nowrap';

export const SourceSection: React.FC<SourceSectionProps> = ({ data: { reservation } }) => {
  const isChannelManager = reservation.source === ReservationSource.CHANNEL_MANAGER;
  const currency = reservation.paymentList?.[0]?.currency;

  const hasAttribution = Boolean(
    reservation.marketingChannel ||
    reservation.utm_source ||
    reservation.utm_campaign ||
    reservation.adType ||
    reservation.gclid ||
    reservation.fbclid
  );
  const clickIds = [
    reservation.gclid && 'gclid',
    reservation.fbclid && 'fbclid',
    reservation.ttclid && 'ttclid'
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
        Source
      </h2>
      <div className="mt-[20px] grid grid-cols-2 sm:grid-cols-4 gap-x-[32px] gap-y-[20px]">
        <Item label="Source" value={getReservationSourceLabel(reservation.source)} />
        <Item
          label="Channel"
          value={reservation.channel ? getReservationChannelLabel(reservation.channel) : undefined}
        />
        {isChannelManager && <Item label="Via" value="Rentals United" />}
        {isChannelManager && currency && <Item label="Currency" value={currency.toUpperCase()} />}
        {reservation.channelCommission?.amount ? (
          <Item
            label="Commission"
            value={
              <NumberFormat
                value={reservation.channelCommission.amount}
                displayType="text"
                thousandSeparator
                prefix={`${(reservation.channelCommission.currency || 'EGP').toUpperCase()} `}
              />
            }
          />
        ) : null}
        {reservation.brokerName && <Item label="Broker" value={reservation.brokerName} />}
        {reservation.promoCodeUsed && <Item label="Promo code" value={reservation.promoCodeUsed} />}
      </div>

      <details className="group mt-[24px] rounded-[12px] bg-creams-100">
        <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer flex items-center justify-between gap-[12px] px-[16px] py-[14px]">
          <span className="flex items-center gap-[10px] flex-wrap min-w-0">
            <span className="text-[15px] font-gilroy-medium text-neutral-500">Attribution</span>
            {hasAttribution ? (
              <>
                <span
                  className={`${chipClassName} ${
                    reservation.marketingChannel &&
                    reservation.marketingChannel !== MarketingChannel.UNKNOWN
                      ? 'bg-primary-50 text-primary-700'
                      : 'bg-neutral-0 text-neutral-300'
                  }`}
                >
                  {getMarketingChannelLabel(reservation.marketingChannel)}
                </span>
                <span
                  className={`${chipClassName} ${
                    reservation.adType === AdType.PAID
                      ? 'bg-action-alert/20 text-neutral-500'
                      : reservation.adType === AdType.ORGANIC
                        ? 'bg-primary-50 text-primary-700'
                        : 'bg-neutral-0 text-neutral-300'
                  }`}
                >
                  {getAdTypeLabel(reservation.adType)}
                </span>
                {reservation.utm_campaign && (
                  <span
                    className="text-[13px] text-neutral-300 truncate max-w-[280px]"
                    title={reservation.utm_campaign}
                  >
                    {reservation.utm_campaign}
                  </span>
                )}
              </>
            ) : (
              <span className="text-[13px] text-neutral-300">Nothing recorded</span>
            )}
          </span>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            className="shrink-0 text-neutral-300 transition-transform group-open:rotate-180"
          />
        </summary>
        <div className="px-[16px] pb-[16px] pt-[4px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-[32px] gap-y-[16px]">
          <Item label="UTM source" value={reservation.utm_source} />
          <Item label="Medium" value={reservation.utm_medium} />
          <Item label="Campaign" value={reservation.utm_campaign} />
          <Item label="Content" value={reservation.utm_content} />
          <Item label="Term" value={reservation.utm_term} />
          <Item
            label="Talked to us via"
            value={
              reservation.messagingChannel
                ? messagingChannelLabels[reservation.messagingChannel]
                : undefined
            }
          />
          <Item label="Landing page" value={reservation.landingPath} />
          <Item label="Referrer" value={reservation.referrer} />
          <Item label="Ad click id" value={clickIds || undefined} />
          <Item
            label="Captured"
            value={
              reservation.attributedAt
                ? moment(Number(reservation.attributedAt)).format('DD/MM/YYYY HH:mm')
                : undefined
            }
          />
        </div>
      </details>
    </>
  );
};
